import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Button,
  Chip,
  useMediaQuery,
  Tooltip,
  Popover,
  MenuItem,
  ListItemText,
  Menu,
  ListItemIcon,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import {
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  useGridApiRef,
} from "@mui/x-data-grid";
import { loadExcelJS } from "helpers/loadExcelJS";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ClearIcon from "@mui/icons-material/ClearRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ColumnFilter from "components/ColumnFilter";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import CEQuickFilter from "components/CEQuickFilter";
import DataGridResponsive, {
  currencyQuickFilterFn,
} from "components/DataGridResponsive";
import QuickFilterField from "components/QuickFilterField";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import { usePageTitle } from "hooks/usePageTitle";
import LinguistPaymentProfile from "scenes/management/linguistsPayments/LinguistPaymentProfile";
import LinguistsPayments1099Process from "scenes/management/linguistsPayments/LinguistsPayments1099Process";
import api from "services/api";
import { numericComparator } from "helpers/ServiceHelpers";
import { NAVBAR_HEIGHT } from "constants/layout";
import { useStateContext } from "contexts/ContextProvider";

// Batch C wave 3 — LinguistsPayments columns whose desktop header enables the
// custom numeric filter inside ColumnFilter (both money columns). Used to build
// the card-mode filter-sheet entries with identical semantics.
const CARD_MODE_CUSTOM_NUMERIC_FILTER_FIELDS = ["AmountLeft", "AmountPaid"];

const LinguistsPayments = () => {
  usePageTitle("Linguist Payments");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { refreshPage, showSnackbar, openDialog, closeDialog } =
    useStateContext();
  const apiRef = useGridApiRef();
  // Preserve the scroll position across refetches (saves bounce to the top)
  const { capture: captureGridScroll, restore: restoreGridScroll } =
    useDataGridScrollRestore(apiRef);

  const [minMaxDates, setMinMaxDates] = useState();

  // Month and Year state
  const months = useMemo(
    () => [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    [],
  );

  const currentYear = dayjs().year();

  const getSavedMonths = () => {
    const saved = window.localStorage.getItem(
      "LinguistsPaymentsSelectedMonths",
    );
    return saved ? JSON.parse(saved) : [dayjs().format("MMM")];
  };

  const [selectedMonths, setSelectedMonths] = useState(getSavedMonths);
  const [tempSelectedMonths, setTempSelectedMonths] = useState(getSavedMonths);

  const getSavedYear = () => {
    const saved = window.localStorage.getItem("LinguistsPaymentsSelectedYear");
    return saved ? JSON.parse(saved) : [String(currentYear)];
  };

  const [yearOptions, setYearOptions] = useState([String(currentYear)]);
  const [selectedYear, setSelectedYear] = useState(getSavedYear);
  const [tempSelectedYear, setTempSelectedYear] = useState(getSavedYear);

  // Data state
  const [linguistPayments, setLinguistPayments] = useState([]);
  const [selectedLinguist, setSelectedLinguist] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [is1099ProcessOpen, setIs1099ProcessOpen] = useState(false);

  const getSavedDensity = () => {
    const saved = localStorage.getItem("LINGUISTS_PAYMENTS_GRID_DENSITY");
    return saved || "compact";
  };

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("LINGUISTS_PAYMENTS_COLUMNS_VISIBLE");
    return saved ? JSON.parse(saved) : {};
  };

  const [density, setDensity] = useState(getSavedDensity);
  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);

  const [gridOptionsOpen, setGridOptionsOpen] = useState(false);
  const [gridOptionsMode, setGridOptionsMode] = useState(null);
  const [gridOptionsAnchorEl, setGridOptionsAnchorEl] = useState(null);

  useEffect(() => {
    localStorage.setItem("LINGUISTS_PAYMENTS_GRID_DENSITY", density);
  }, [density]);

  useEffect(() => {
    localStorage.setItem(
      "LINGUISTS_PAYMENTS_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  // Default ordering: A-Z by name, but move these linguists to the bottom
  const DEPRIORITIZED_LINGUISTS = useMemo(
    () =>
      new Set(["amer al fayadh", "boostlingo", "fiverr", "lti", "smartcat"]),
    [],
  );

  const sortLinguistPayments = useCallback(
    (data) => {
      return [...(data || [])].sort((a, b) => {
        const aName = (a?.LinguistName ?? "").toString().trim();
        const bName = (b?.LinguistName ?? "").toString().trim();
        const aDep = DEPRIORITIZED_LINGUISTS.has(aName.toLowerCase());
        const bDep = DEPRIORITIZED_LINGUISTS.has(bName.toLowerCase());
        if (aDep !== bDep) return aDep ? 1 : -1; // non-deprioritized first
        return aName.localeCompare(bName, undefined, { sensitivity: "base" });
      });
    },
    [DEPRIORITIZED_LINGUISTS],
  );

  // Selection state for long-press
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const LONG_PRESS_MS = 500;

  // Filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  // Handle date selection save
  const handleDateSelectionSave = (newSelection) => {
    // Ensure only one year is selected
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1),
    };

    // Update state
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);

    // Save to localStorage
    window.localStorage.setItem(
      "LinguistsPaymentsSelectedMonths",
      JSON.stringify(updatedSelection.months),
    );
    window.localStorage.setItem(
      "LinguistsPaymentsSelectedYear",
      JSON.stringify(updatedSelection.years),
    );

    //TO DO ADD DATA REFETCH IN HERE
    //INTERPRETATIONS.JSX: fetchInterpretationsData(updatedSelection.months, updatedSelection.years);
  };

  // Fetch linguist payments for selected months/year
  const fetchLinguistPayments = useCallback(async () => {
    setIsLoading(true);
    captureGridScroll();
    try {
      const params = new URLSearchParams();

      // Convert month names to indices (0-11)
      selectedMonths.forEach((monthName) => {
        const monthIndex = months.indexOf(monthName);
        if (monthIndex !== -1) {
          params.append("months", monthIndex);
        }
      });
      const year = Array.isArray(selectedYear) ? selectedYear[0] : selectedYear;
      params.append("year", year);

      const response = await api.get(
        `/linguists/payments?${params.toString()}`,
      );

      const sortedPayments = sortLinguistPayments(response.data);
      setLinguistPayments(sortedPayments);
      restoreGridScroll(sortedPayments);
      setSelectedLinguist(null);
    } catch (error) {
      console.error("Error fetching linguist payments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    months,
    selectedMonths,
    selectedYear,
    sortLinguistPayments,
    captureGridScroll,
    restoreGridScroll,
  ]);

  // Handle linguist row click - open overlay
  const handleLinguistRowClick = (params) => {
    const linguist = linguistPayments.find((l) => l.LinguistID === params.id);
    setSelectedLinguist(linguist);
    setIsOverlayOpen(true);
  };

  const handleCloseProfile = (shouldRefresh) => {
    setIsOverlayOpen(false);
    if (shouldRefresh) {
      fetchLinguistPayments();
    }
  };

  // Long-press handlers
  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectedRows((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectedRows((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
  };

  const handleCancelSelection = useCallback(() => {
    setSelectionEnabled(false);
    setSelectedRows([]);
    suppressNextClickRef.current = false;
  }, []);

  // Mark a single linguist's unpaid services as paid
  const handleMarkAsPaidForLinguist = async (linguistId) => {
    try {
      const linguist = linguistPayments.find(
        (l) => l.LinguistID === linguistId,
      );
      const unpaidServiceIds = (linguist?.LinguistServices || [])
        .filter((s) => !s?.LinguistPaid)
        .map((s) => s.id);

      if (!unpaidServiceIds || unpaidServiceIds.length === 0) {
        showSnackbar("No unpaid services to mark", theme);
        return;
      }

      await api.put("/services/mark-linguist-paid", {
        serviceIds: unpaidServiceIds,
      });

      showSnackbar(
        `${unpaidServiceIds.length} service(s) marked as paid`,
        theme,
      );
      // Refresh data to reflect changes
      fetchLinguistPayments();
    } catch (error) {
      console.error("Error marking services as paid:", error);
      showSnackbar(
        error.response?.data?.error || "Failed to mark services as paid",
        theme,
      );
    }
  };

  const handleMarkAsPayd = useCallback(async () => {
    if (selectedRows.length === 0) return;

    try {
      const linguistsWithServices = selectedRows
        .map((linguistId) => {
          const linguist = linguistPayments.find(
            (l) => l.LinguistID === linguistId,
          );
          return linguist?.LinguistServices?.map((service) => service.id) || [];
        })
        .flat();

      if (linguistsWithServices.length === 0) {
        showSnackbar("No services found for selected linguists", theme);
        return;
      }

      await api.put("/services/mark-linguist-paid", {
        serviceIds: linguistsWithServices,
      });

      showSnackbar(
        `${linguistsWithServices.length} service(s) marked as paid`,
        theme,
      );
      handleCancelSelection();
      await fetchLinguistPayments();
    } catch (error) {
      console.error("Error marking services as paid:", error);
      showSnackbar(
        error.response?.data?.error || "Failed to mark services as paid",
        theme,
      );
    }
  }, [
    selectedRows,
    linguistPayments,
    showSnackbar,
    theme,
    handleCancelSelection,
    fetchLinguistPayments,
  ]);

  // Handle copy actions
  const handleCopyName = (linguistName, event) => {
    event.stopPropagation();
    navigator.clipboard.writeText(linguistName);
    showSnackbar(`Copied: ${linguistName}`, theme);
  };

  const handleCopyAmount = (amount, event) => {
    event.stopPropagation();
    navigator.clipboard.writeText(amount.toFixed(2));
    showSnackbar(`Copied: $${amount.toFixed(2)}`, theme);
  };

  const handleOpenProfile = (linguistId, event) => {
    event.stopPropagation();
    navigate(`/linguists/${linguistId}`);
  };

  // Card-mode per-row actions (Amine 2026-10-05): rendered by the wrapper
  // inside each card's ⋮ Menu (`mobile.cardActions`). Same four actions as
  // the desktop actions column — keep the two in sync — but the desktop
  // renderCell stays untouched per the round's constraint.
  const buildCardActions = (row, closeMenu) => {
    const linguist = linguistPayments.find((l) => l.LinguistID === row.id);
    const services = linguist?.LinguistServices || [];
    const unpaidCount = services.filter((s) => !s?.LinguistPaid).length;
    const disabled = unpaidCount === 0;

    // Assignments still in a "Confirmed" status (starts with "Confirmed") —
    // same guard the desktop chip applies before allowing "Mark as Paid".
    const confirmedServices = services.filter((s) => {
      const status =
        s?.InterpretationStatus ||
        s?.OtherServiceStatus ||
        s?.TranslationStatus ||
        "";
      return status.startsWith("Confirmed");
    });
    const hasConfirmed = confirmedServices.length > 0;
    const confirmedNames = confirmedServices
      .map((s) => s.ProjectNumber || "Unknown")
      .join(", ");

    return (
      <>
        <MenuItem
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            closeMenu();
            if (hasConfirmed) {
              showSnackbar(
                `Cannot mark as paid — the following assignment(s) are not completed yet: ${confirmedNames}`,
                theme,
                "error",
              );
              return;
            }
            handleMarkAsPaidForLinguist(row.id);
          }}
          sx={hasConfirmed ? { color: theme.palette.warning.main } : undefined}
        >
          <ListItemIcon>
            <PaidRoundedIcon
              fontSize="small"
              sx={
                hasConfirmed ? { color: theme.palette.warning.main } : undefined
              }
            />
          </ListItemIcon>
          <ListItemText>Mark as Paid</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            closeMenu();
            handleOpenProfile(row.id, event);
          }}
        >
          <ListItemIcon>
            <PersonRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open Profile</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            closeMenu();
            handleCopyName(row.Name, event);
          }}
        >
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Name</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            closeMenu();
            handleCopyAmount(row.AmountLeft, event);
          }}
        >
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Amount</ListItemText>
        </MenuItem>
      </>
    );
  };

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    const upperFieldName = fieldName.toUpperCase();
    return `LINGUISTS_PAYMENTS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    const fields = [
      "Name",
      "NumberOfAssignments",
      "Rate",
      "InPersonAssignments",
      "VirtualAssignments",
      "TotalMiles",
      "AmountLeft",
      "AmountPaid",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "Name",
      "NumberOfAssignments",
      "Rate",
      "InPersonAssignments",
      "VirtualAssignments",
      "TotalMiles",
      "AmountLeft",
      "AmountPaid",
    ];
    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  // Fetch data on mount and when selections change
  useEffect(() => {
    fetchLinguistPayments();
  }, [fetchLinguistPayments, refreshPage]);

  // Fetch data lists including minMaxDates
  const getDataLists = () => {
    api
      .get("/dashboard/serviceDateRange")
      .then((response) => {
        if (response) {
          setMinMaxDates(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching date range:", error);
      });
  };

  // Generate year options when minMaxDates changes
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;

      // Create an array of years between startYear and endYear (inclusive)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }

      setYearOptions(years);

      // If the current selected year is not in the range, update it to the current year
      // or the closest year in the range
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          // Choose the closest year in range to the current year
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }

          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]);

  // Load data lists on mount
  useEffect(() => {
    getDataLists();
  }, []);

  // Map linguist payments data for grid
  const linguistPaymentRows = useMemo(
    () =>
      linguistPayments.map((payment) => ({
        id: payment.LinguistID,
        Name: payment.LinguistName,
        AmountLeft: payment.AmountLeft ?? payment.LinguistTotal ?? 0,
        AmountPaid: payment.AmountPaid ?? 0,
        NumberOfAssignments: payment.NumberOfAssignments || 0,
        Rate: payment.Rate || "",
        InPersonAssignments: payment.InPersonAssignments || 0,
        VirtualAssignments: payment.VirtualAssignments || 0,
        TotalMiles: payment.TotalMiles || 0,
      })),
    [linguistPayments],
  );

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      { field: "Name", valueGetter: (_, row) => row?.Name || "" },
      {
        field: "NumberOfAssignments",
        valueGetter: (_, row) => row?.NumberOfAssignments?.toString() || "0",
      },
      {
        field: "Rate",
        valueGetter: (_, row) => row?.Rate || "",
      },
      {
        field: "InPersonAssignments",
        valueGetter: (_, row) => row?.InPersonAssignments?.toString() || "0",
      },
      {
        field: "VirtualAssignments",
        valueGetter: (_, row) => row?.VirtualAssignments?.toString() || "0",
      },
      {
        field: "TotalMiles",
        valueGetter: (_, row) =>
          row?.TotalMiles ? parseFloat(row.TotalMiles).toFixed(2) : "0.00",
      },
      {
        field: "AmountLeft",
        valueGetter: (_, row) =>
          row?.AmountLeft
            ? `$${parseFloat(row.AmountLeft).toFixed(2)}`
            : "$0.00",
      },
      {
        field: "AmountPaid",
        valueGetter: (_, row) =>
          row?.AmountPaid
            ? `$${parseFloat(row.AmountPaid).toFixed(2)}`
            : "$0.00",
      },
    ],
    [],
  );

  // Column filters using custom hook
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(linguistPaymentRows || [], filterColumns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback(
    (field, values) => {
      handleColumnFilterChange(field, values);
      saveColumnFilter(field, values);
    },
    [handleColumnFilterChange, saveColumnFilter],
  );

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
  }, [clearAllFilters, clearAllColumnFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!linguistPaymentRows || linguistPaymentRows.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [linguistPaymentRows, loadAllColumnFilters]);

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return linguistPaymentRows || [];
      }

      return (linguistPaymentRows || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;
          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;
          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, linguistPaymentRows, filterColumns]);

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  // Linguist DataGrid columns with actions
  const linguistPaymentColumns = [
    {
      field: "Name",
      headerName: "Linguist Name",
      flex: 2,
      minWidth: 200,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "NumberOfAssignments",
      headerName: "# of Assignments",
      flex: 1.7,
      minWidth: 170,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[1] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Rate",
      headerName: "Rate",
      flex: 1.0,
      minWidth: 100,
      valueGetter: (_, params) => params.Rate || "",
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "InPersonAssignments",
      headerName: "In-Person",
      flex: 1.25,
      minWidth: 125,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "VirtualAssignments",
      headerName: "Virtual",
      flex: 1,
      minWidth: 100,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalMiles",
      headerName: "Total Miles",
      flex: 1.5,
      minWidth: 150,
      valueGetter: (_, params) => {
        return params.TotalMiles
          ? parseFloat(params.TotalMiles).toFixed(2)
          : "0.00";
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AmountLeft",
      headerName: "Amount Left",
      sortComparator: numericComparator,
      flex: 1.5,
      minWidth: 150,
      valueGetter: (_, params) =>
        params.AmountLeft
          ? `$${parseFloat(params.AmountLeft).toFixed(2)}`
          : "$0.00",
      // Quick filter parity (Amine 2026-10-01): "1500" / "1,500" match the
      // displayed "$1,500.00" on the desktop CEQuickFilter + card search.
      getApplyQuickFilterFn: currencyQuickFilterFn,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontWeight: Number(params.row.AmountLeft) === 0 ? 400 : 700,
              color:
                Number(params.row.AmountLeft) === 0
                  ? theme.palette.primary.text2
                  : theme.palette.error.main,
            }}
          >
            {params.formattedValue}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            enableCustomNumericFilter={true}
            data={filterData[6] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AmountPaid",
      headerName: "Amount Paid",
      sortComparator: numericComparator,
      flex: 1.5,
      minWidth: 150,
      valueGetter: (_, params) =>
        params.AmountPaid
          ? `$${parseFloat(params.AmountPaid).toFixed(2)}`
          : "$0.00",
      // Quick filter parity (Amine 2026-10-01): "1500" / "1,500" match the
      // displayed "$1,500.00" on the desktop CEQuickFilter + card search.
      getApplyQuickFilterFn: currencyQuickFilterFn,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontWeight: Number(params.row.AmountPaid) === 0 ? 400 : 700,
              color:
                Number(params.row.AmountPaid) === 0
                  ? theme.palette.primary.text2
                  : theme.palette.primary.main,
            }}
          >
            {params.formattedValue}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            enableCustomNumericFilter={true}
            data={filterData[7] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 3.4,
      minWidth: 360,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            justifyContent: "flex-start",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          {(() => {
            const linguist = linguistPayments.find(
              (l) => l.LinguistID === params.row.id,
            );
            const services = linguist?.LinguistServices || [];
            const unpaidCount = services.filter(
              (s) => !s?.LinguistPaid,
            ).length;
            const disabled = unpaidCount === 0;

            // Find assignments still in a "Confirmed" status (starts with "Confirmed")
            const confirmedServices = services.filter((s) => {
              const status =
                s?.InterpretationStatus ||
                s?.OtherServiceStatus ||
                s?.TranslationStatus ||
                "";
              return status.startsWith("Confirmed");
            });
            const hasConfirmed = confirmedServices.length > 0;

            const chip = (
              <Chip
                label="Mark as Paid"
                size="small"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasConfirmed) {
                    const confirmedNames = confirmedServices
                      .map((s) => s.ProjectNumber || "Unknown")
                      .join(", ");
                    showSnackbar(
                      `Cannot mark as paid — the following assignment(s) are not completed yet: ${confirmedNames}`,
                      theme,
                      "error",
                    );
                    return;
                  }
                  handleMarkAsPaidForLinguist(params.row.id);
                }}
                sx={{
                  backgroundColor: hasConfirmed
                    ? theme.palette.warning.main
                    : theme.palette.primary.main,
                  color: theme.palette.background.default,
                  fontSize: "0.75rem",
                  height: "24px",
                  cursor: disabled ? "default" : "pointer",
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: hasConfirmed
                      ? theme.palette.warning.dark
                      : theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              />
            );

            if (hasConfirmed) {
              const confirmedNames = confirmedServices
                .map((s) => s.ProjectNumber || "Unknown")
                .join(", ");
              return (
                <Tooltip
                  title={`Cannot pay yet — the following assignment(s) are not completed: ${confirmedNames}`}
                  arrow
                >
                  <span>{chip}</span>
                </Tooltip>
              );
            }

            return chip;
          })()}

          <Chip
            label="Open Profile"
            size="small"
            onClick={(e) => handleOpenProfile(params.row.id, e)}
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.primary.text,
              fontSize: "0.75rem",
              height: "24px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: theme.palette.primary.text3,
              },
            }}
          />
          <Chip
            label="Copy Name"
            size="small"
            onClick={(e) => handleCopyName(params.row.Name, e)}
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.primary.text,
              fontSize: "0.75rem",
              height: "24px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: theme.palette.primary.text3,
              },
            }}
          />
          <Chip
            label="Copy Amount"
            size="small"
            onClick={(e) => handleCopyAmount(params.row.AmountLeft, e)}
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.primary.text,
              fontSize: "0.75rem",
              height: "24px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: theme.palette.primary.text3,
              },
            }}
          />
        </Box>
      ),
    },
  ];

  const filteredLinguistPaymentColumns = linguistPaymentColumns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const exportableColumns = linguistPaymentColumns.filter(
    (col) => col.field !== "actions",
  );

  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {gridOptionsMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setGridOptionsOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {gridOptionsMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={
                !linguistPaymentColumns.some(
                  (col) => columnsVisible[col.field] === false,
                )
              }
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {linguistPaymentColumns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const visibleColumns =
                apiRef.current
                  ?.getVisibleColumns?.()
                  ?.filter((col) => col.field !== "actions") ??
                exportableColumns.filter(
                  (col) => columnsVisible[col.field] !== false,
                );

              if (visibleColumns.length === 0) {
                showSnackbar("No visible columns to export", theme, "warning");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                showSnackbar("No data available to export", theme, "warning");
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              const csvRows = columnFilteredData.map((row) =>
                visibleColumns
                  .map((col) => {
                    const value = col.valueGetter
                      ? col.valueGetter(undefined, row)
                      : row[col.field];
                    const strValue = String(value ?? "").replace(/"/g, '""');
                    return `"${strValue}"`;
                  })
                  .join(","),
              );

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `linguists-payments-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("CSV export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={async () => {
            try {
              const visibleColumns =
                apiRef.current
                  ?.getVisibleColumns?.()
                  ?.filter((col) => col.field !== "actions") ??
                exportableColumns.filter(
                  (col) => columnsVisible[col.field] !== false,
                );

              if (visibleColumns.length === 0) {
                showSnackbar("No visible columns to export", theme, "warning");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                showSnackbar("No data available to export", theme, "warning");
                return;
              }

              showSnackbar("Preparing XLSX export…", theme, "info");

              const ExcelJS = await loadExcelJS();
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Linguist Payments");

              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              const columnWidths = new Array(visibleColumns.length).fill(15);

              columnFilteredData.forEach((row) => {
                const rowData = visibleColumns.map((col, colIndex) => {
                  const value = col.valueGetter
                    ? col.valueGetter(undefined, row)
                    : row[col.field];
                  const strValue = String(value ?? "");
                  columnWidths[colIndex] = Math.max(
                    columnWidths[colIndex],
                    Math.min(strValue.length + 2, 50),
                  );
                  return strValue;
                });
                worksheet.addRow(rowData);
              });

              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `linguists-payments-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
              showSnackbar(
                "Couldn't prepare the XLSX export — check your connection and try again.",
                theme,
                "error",
              );
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  // Export dialog bridge — keep the dialog factory fresh at click time. The
  // export MenuItem lives inside a memoized toolbar, so it captures
  // <ExportDialogContent /> from the render where that memo last recomputed —
  // which can predate the data load (stale closure → the dialog says
  // "No data available to export"). This ref is reassigned every commit, so
  // the click always opens the latest render's dialog with current data.
  const openExportDialogRef = useRef(null);
  useEffect(() => {
    openExportDialogRef.current = () =>
      openDialog({
        title: "Export Data",
        theme,
        customContent: <ExportDialogContent />,
      });
  });

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  // Calculate total payments
  let totalPayments = 0;
  (columnFilteredData || []).forEach((payment) => {
    totalPayments += Number(payment.AmountLeft ?? 0);
  });

  // Calculate total amount paid
  let totalAmountPaid = 0;
  (columnFilteredData || []).forEach((payment) => {
    totalAmountPaid += Number(payment.AmountPaid ?? 0);
  });

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters,
      onClear,
      theme,
      cardMode,
      quickFilterValue,
      onQuickFilterChange,
    }) => {
      const isMobileToolbar = isMobile;
      const markLabel =
        selectedRows.length > 1 ? "Mark All as Paid" : "Mark as Paid";

      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      const selectionActions = [];
      if (selectionEnabled) {
        selectionActions.push({
          label: "Cancel Selection",
          onClick: () => {
            handleCancelSelection();
            handleClose();
          },
        });
        selectionActions.push({
          label: markLabel,
          disabled: selectedRows.length === 0,
          onClick: () => {
            handleMarkAsPayd();
            handleClose();
          },
        });
      }

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobileToolbar && selectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={selectedRows.length === 0}
                onClick={handleMarkAsPayd}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                {markLabel}
              </Button>
            </>
          )}
          {isMobileToolbar && selectionActions.length > 0 && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  {selectionActions.map((action, index) => (
                    <MenuItem
                      key={index}
                      onClick={action.onClick}
                      disabled={action.disabled}
                    >
                      <ListItemText>{action.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Box>
              </Popover>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />

          {!isMobileToolbar && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {hasAnyActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobileToolbar && hasAnyActiveFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            {cardMode ? (
              <QuickFilterField
                iconOnly
                value={quickFilterValue}
                onChange={onQuickFilterChange}
              />
            ) : (
              <CEQuickFilter iconOnly={isMobileToolbar} />
            )}

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: theme.palette.grey.main,
                  borderColor: theme.palette.divider,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setGridOptionsMode("density");
                  setGridOptionsOpen(true);
                  setGridOptionsAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setGridOptionsMode("columns");
                  setGridOptionsOpen(true);
                  setGridOptionsAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openExportDialogRef.current?.();
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    selectionEnabled,
    selectedRows.length,
    isMobile,
    handleCancelSelection,
    handleMarkAsPayd,
  ]);

  // Batch C wave 3 — hoisted out of customFooter so the SAME element tree
  // renders in both the desktop grid footer and card mode (mobile.footer).
  const totalsStrip = (
    <Box
      sx={{
        p: "1rem",
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexWrap: "wrap", justifyContent: "center" }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total Amount Left:
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme?.palette?.secondary?.[400],
              fontWeight: 600,
            }}
          >
            $
            {Number(totalPayments).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total Amount Paid:
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme?.palette?.secondary?.[400],
              fontWeight: 600,
            }}
          >
            $
            {Number(totalAmountPaid).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );

  // Custom footer component
  const customFooter = () => {
    return (
      <GridFooterContainer>
        {totalsStrip}

        <GridFooter
          sx={{
            border: "none",
          }}
        />
      </GridFooterContainer>
    );
  };

  // ── Batch C wave 3 — card-mode filter sheet ──────────────────────────────
  // One entry per visible column that carries a header ColumnFilter, built
  // from the exact same props as those header renderers so filter semantics
  // are identical between modes. In card mode the desktop headers are not
  // rendered, so these sheet copies are the only live filter instances.
  const buildMobileFilterNode = (field) => {
    const colDef = filteredLinguistPaymentColumns.find(
      (col) => col.field === field,
    );
    const dataIndex = filterColumns.findIndex((col) => col.field === field);
    return (
      // The header filters keep their trigger at opacity 0 until the column
      // header is hovered — impossible inside a touch sheet, so the sheet copy
      // forces the trigger visible (the desktop headers are untouched).
      <Box sx={{ "& .MuiIconButton-root": { opacity: 1 } }}>
        <ColumnFilter
          data={dataIndex >= 0 ? filterData[dataIndex] || [] : []}
          field={field}
          valueGetter={colDef?.valueGetter}
          selectedValues={columnFilters[field] || []}
          onFilterChange={(values) =>
            handleColumnFilterChangeWithStorage(field, values)
          }
          onSortChange={(field, direction) => {
            const newSortModel = direction
              ? [{ field, sort: direction }]
              : [];
            setSortModel(newSortModel);
            handleSortModelChange(newSortModel);
          }}
          currentSort={sortModel.find((s) => s.field === field)?.sort || null}
          // 1:1 with the desktop Amount Left / Amount Paid headers, which
          // enable the custom numeric filter inside ColumnFilter.
          {...(CARD_MODE_CUSTOM_NUMERIC_FILTER_FIELDS.includes(field)
            ? { enableCustomNumericFilter: true }
            : {})}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
        />
      </Box>
    );
  };

  const mobileFilterEntries = filteredLinguistPaymentColumns
    .filter((col) =>
      filterColumns.some((filterCol) => filterCol.field === col.field),
    )
    .map((col) => {
      const selected = columnFilters[col.field] || [];
      return {
        field: col.field,
        headerName: col.headerName || col.field,
        summary: selected.length > 0 ? `${selected.length} selected` : "All",
        active: selected.length > 0,
        node: buildMobileFilterNode(col.field),
      };
    });

  const mobileActiveFilterCount = mobileFilterEntries.filter(
    (entry) => entry.active,
  ).length;

  // Chips row (design §3b): one removable chip per active column filter, plus a
  // trailing "Clear all" chip when 2+ filters are active (same semantics as
  // the sheet / toolbar clear). The wrapper adds the search chip itself.
  const mobileFilterChips = mobileFilterEntries
    .filter((entry) => entry.active)
    .map((entry) => ({
      key: `column-filter-${entry.field}`,
      label: `${entry.headerName}: ${columnFilters[entry.field].length} selected`,
      onClear: () => handleColumnFilterChangeWithStorage(entry.field, []),
    }));

  if (mobileFilterChips.length >= 2) {
    mobileFilterChips.push({
      key: "column-filters-clear-all",
      label: "Clear all",
      onClear: handleToolbarClear,
    });
  }

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        // 100dvh where supported: bottom controls stay clear of iOS Safari's collapsing chrome
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="space-between"
            sx={{ px: 1 }}
          >
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              gap={2}
              justifyContent="space-between"
            >
              <Box sx={{ width: "100%", maxWidth: FILTER_SELECTOR_WIDTH }}>
                <CustomDateSelector
                  label="Date Range"
                  months={months}
                  years={
                    yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                  }
                  selectedMonths={tempSelectedMonths}
                  selectedYears={tempSelectedYear}
                  onSave={handleDateSelectionSave}
                  isMultiMonth={true}
                  isMultiYear={false}
                  monthGridItemSize={3}
                  yearGridItemSize={4}
                  showClearFilter={false}
                  iconOnly={isMobile}
                />
              </Box>

              {isMobile ? (
                <Tooltip title="Form 1099" placement="top">
                  <IconButton
                    onClick={() => setIs1099ProcessOpen(true)}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      borderRadius: "12px",
                      border: "none",
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <AssignmentRoundedIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  onClick={() => setIs1099ProcessOpen(true)}
                  startIcon={<AssignmentRoundedIcon />}
                  sx={{
                    width: "200px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.background.default,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.primary.main,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHover,
                      color: theme.palette.primary.main,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>Form 1099</Box>
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          boxShadow: theme.shadows[3],
        }}
      >
        <DataGridResponsive
          loading={isLoading}
          mobile={{
            // Card mode (mobile) config — desktop grid ignores this prop
            primaryField: "Name",
            // Scanning signal for a payments screen: Amount Left at the
            // top-right (renders the column's own color-coded renderCell).
            // Round 3 (2026-10-05, Amine): this chip is THE single money
            // figure of the card — see hiddenFields below.
            badgeField: "AmountLeft",
            // Card-body exclusions (the wrapper reuses this list for the
            // card-mode sort sheet too — DataGridResponsive sortSheetColumns):
            //  - "actions": the chips column — its actions are reachable from
            //    each card's ⋮ menu instead (`cardActions` below).
            //  - "AmountPaid" (round 3 — Amine: "the Total shown in the big
            //    card is different than the Amount, and the amount is also
            //    visible when I click on the show X more button, while it
            //    shouldn't be"). The collapsed card's big number is the
            //    Amount Left chip; the "Amount Paid" row put a second,
            //    different money figure in the expanded body. Dedup: one
            //    money value per card. Amount Paid stays on the desktop
            //    grid + card-mode filter sheet.
            hiddenFields: ["actions", "AmountPaid"],
            // 2026-10-05 (Amine): the collapsed card shows headline (Name) +
            // the Amount Left chip only; the five other data columns fold
            // behind the "Show N more" expander (wrapper flag `collapseBody`).
            // Totals footer stays pinned below the card scroller
            // (mobile.footer, untouched).
            collapseBody: true,
            // Per-row actions on the card's ⋮ button — same four actions as
            // the desktop actions column (Mark as Paid / Open Profile /
            // Copy Name / Copy Amount), same disabled/confirmed semantics.
            cardActions: buildCardActions,
            pageSize: 20,
            selectable: true,
            // Batch C wave 3 — card-mode filter sheet: entries reuse the exact
            // header ColumnFilter props, activeCount drives the filter-badge,
            // and Clear all reuses the existing toolbar clear (column filters +
            // quick filter, design §3b).
            filters: mobileFilterEntries,
            activeCount: mobileActiveFilterCount,
            onClearFilters: handleToolbarClear,
            // Totals strip: the same element tree as the desktop grid footer
            // (hoisted above into `totalsStrip`).
            footer: totalsStrip,
            // Removable chips under the control row (wrapper adds the search
            // chip itself).
            chips: mobileFilterChips,
          }}
          rows={columnFilteredData || []}
          columns={filteredLinguistPaymentColumns}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) =>
            setSelectedRows(newSelection)
          }
          onRowClick={(params, event, details) => {
            if (suppressNextClickRef.current) {
              event.defaultMuiPrevented = true;
              suppressNextClickRef.current = false;
              return;
            }
            if (openFilter) {
              setOpenFilter(null);
              return;
            }
            if (selectionEnabled) {
              // In selection mode, suppress the first click after long-press to avoid deselection
              return;
            }
            handleLinguistRowClick(params);
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          disableColumnMenu
          disableColumnSorting
          disableRowSelectionOnClick={!!openFilter}
          apiRef={apiRef}
          slots={{ toolbar: CustomToolbar, footer: customFooter }}
          slotProps={{
            row: {
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: handleToolbarClear,
              theme,
            },
          }}
          ignoreDiacritics
          density={density}
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",
            height: "100%",
            flex: 1,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            // Limit global button color styling to outlined buttons to avoid
            // overriding contained action buttons inside the grid.
            "& .MuiButton-outlined": {
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              backgroundColor:
                selectionEnabled &&
                selectedRows.includes(
                  columnFilteredData?.find(
                    (row) => row.id === columnFilteredData[0]?.id,
                  )?.id,
                )
                  ? theme.palette.action.selected
                  : "inherit",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            },
          }}
        />
      </Box>

      {gridOptionsOpen && gridOptionsMode && (
        <Popover
          open={gridOptionsOpen}
          anchorReference="anchorPosition"
          anchorPosition={
            gridOptionsAnchorEl
              ? { left: window.innerWidth / 2, top: window.innerHeight / 2 }
              : undefined
          }
          onClose={() => setGridOptionsOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              maxWidth: "calc(100vw - 2rem)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}

      {/* Overlay */}
      {isOverlayOpen && (
        <>
          {/* Backdrop */}
          <Box
            onClick={() => setIsOverlayOpen(false)}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
            }}
          />
          {/* Overlay Component */}
          <LinguistPaymentProfile
            linguistData={selectedLinguist}
            onClose={handleCloseProfile}
          />
        </>
      )}

      {/* 1099 Process Overlay */}
      <LinguistsPayments1099Process
        open={is1099ProcessOpen}
        onClose={() => setIs1099ProcessOpen(false)}
        linguistPayments={linguistPayments}
        selectedMonths={selectedMonths}
        selectedYear={selectedYear}
      />
    </Box>
  );
};

export default LinguistsPayments;
